import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useGetSuppliers } from "hooks/useSuppliers";

import Flatpickr from "react-flatpickr";
import "react-datepicker/dist/react-datepicker.css";
import "assets/scss/datatables.scss";
import { toast } from "react-toastify";
import { AppRoutes } from "../../constants";
import { useGetWeighBillyId, useUpdateWeighBill } from "hooks/useWeighBill";
import { weighbillValidationSchema } from "utils";
import { UpdateWeighbillItemPayload, WeighbillType } from "types";
import { BaseInput } from "Components";
import { useGetBranches } from "hooks/useBranches";
import Spinners from "Components/Common/Spinner";
import { AddWeighBillItemModal, WeighbillItemsTable } from "./components";

const EditWeighBill = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { data: weighBillData, isLoading } = useGetWeighBillyId(id || "");

  // add branches, categories, and suppliers
  const { data: branchData } = useGetBranches();
  const { data: supplierData } = useGetSuppliers();
  const { mutate } = useUpdateWeighBill();
  const [loading, setLoading] = useState<boolean>(false);
  const [showWeighBillItemModal, setShowWeighBillItemModal] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editDetails, setEditDetails] = useState<UpdateWeighbillItemPayload>({
    product: { id: 0 },
    cost_price: 0,
    quantity: 0,
    discount_percentage: 0,
    round_up: 0,
    weight: 0,
  });
  const [billItems, setBillItems] = useState<any[]>([]);
  const [itemIndex, setItemIndex] = useState<number>(0);

  const handleCancel = () => {
    // Replace with your desired link
    navigate(AppRoutes.WEIGHBILLS);
  };

  const formik = useFormik<Partial<WeighbillType>>({
    initialValues: {
      weight: 0,
      supplier_id: 0,
      origin: "",
      driver_name: "",
      vehicle_number: "",
      tracking_number: "",
      payment_status: "",
      remarks: "",
      proof_of_delivery: "",
      expected_delivery_date: "",
      status: "",
      weighbill_items: [],
    },
    validationSchema: weighbillValidationSchema,

    onSubmit: async (values) => {
      if (billItems.length === 0) {
        toast.error("Please add at least one weighbill item");
        return;
      }
      setLoading(true);

      // Build the payload
      const payload: any = {
        weight: values.weight,
        supplier_id: values.supplier_id,
        origin: values.origin,
        driver_name: values.driver_name,
        vehicle_number: values.vehicle_number,
        tracking_number: values.tracking_number,
        payment_status: values.payment_status,
        remarks: values.remarks,
        proof_of_delivery: values.proof_of_delivery,
        expected_delivery_date: values.expected_delivery_date,
        status: values.status,
        weighbill_items: (values.weighbill_items || []).map((item) => ({
          ...item,
          product_id: item.product?.id | item?.product_id,
        })),
      };

      if (!id) {
        return;
      }

      mutate(
        { id: id!, data: payload },
        {
          onSuccess: (data) => {
            toast.success("WeighBill updated successfully");
            formik.resetForm();
            setLoading(false);
            navigate(AppRoutes.WEIGHBILLS);
          },
          onError: (error) => {
            setLoading(false);
          },
        }
      );
    },
  });

  useEffect(() => {
    if (weighBillData) {
      formik.setValues({
        weight: weighBillData.weight,
        supplier_id: weighBillData.supplier_id,
        origin: weighBillData.origin,
        driver_name: weighBillData.driver_name,
        vehicle_number: weighBillData.vehicle_number,
        tracking_number: weighBillData.tracking_number,
        payment_status: weighBillData.payment_status,
        remarks: weighBillData.remarks,
        proof_of_delivery: weighBillData.proof_of_delivery,
        expected_delivery_date: weighBillData.expected_delivery_date,
        status: weighBillData.status,
        branch_id: weighBillData?.branch_id,
        weighbill_items: weighBillData?.weighbill_items,
      });

      setBillItems(weighBillData.weighbill_items);
    }
  }, [weighBillData]);

  const toggle = useCallback(() => {
    setShowWeighBillItemModal(!showWeighBillItemModal);
    // if (!showWeighBillItemModal) {
    //   // setSelectedItem(null);
    //   setIsEdit(false);
    // }
  }, [showWeighBillItemModal]);

  useEffect(() => {
    formik.setFieldValue("weighbill_items", billItems);
  }, [billItems]);

  const handleToggleModal = useCallback(
    (arg?: any) => {
      // setBill(arg);
      // setIsEdit(true);
      toggle();
    },
    [toggle]
  );

  const onClickEdit = (data, index) => {
    setEditDetails(data);
    setItemIndex(index);
    setIsEdit(true);
  };

  const onClickDelete = (data) => {
    const updatedBillItems = billItems.filter((item, index) => index !== data);
    toast.success("Weighbill Item deleted.");
    setBillItems(updatedBillItems);
  };

  useEffect(() => {
    if (isEdit) {
      toggle();
    }
  }, [isEdit]);

  if (isLoading) {
    return <Spinners />;
  }

  return (
    <React.Fragment>
      <div className={`page-content`}>
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Weighbill" breadcrumbItem="Edit Weighbill" />

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
            autoComplete="off"
          >
            <Row>
              <Col xs={12}>
                <Card>
                  <CardBody>
                    <CardTitle tag="h4">Weigh Bill Details</CardTitle>
                    <Row>
                      <Col sm="6">
                        <div className="mb-3">
                          <Label htmlFor="branch_id">Branch*</Label>
                          <Input
                            type="select"
                            name="branch_id"
                            onChange={(e) =>
                              formik.setFieldValue("branch_id", e.target.value)
                            }
                            value={formik.values.branch_id}
                            invalid={
                              !!(
                                formik.touched.branch_id &&
                                formik.errors.branch_id
                              )
                            }
                          >
                            <option value="">Select Branch...</option>
                            {branchData &&
                              branchData.map((branch) => (
                                <option key={branch.id} value={branch.id}>
                                  {branch.name}
                                </option>
                              ))}
                          </Input>
                          {formik.touched.branch_id &&
                            formik.errors.branch_id && (
                              <FormFeedback>
                                {formik.errors.branch_id}
                              </FormFeedback>
                            )}
                        </div>
                      </Col>
                      <Col sm="6">
                        <div className="mb-3">
                          <Label htmlFor="supplier_id">Supplier*</Label>
                          <Input
                            type="select"
                            name="supplier_id"
                            onChange={(e) =>
                              formik.setFieldValue(
                                "supplier_id",
                                e.target.value
                              )
                            }
                            value={formik.values.supplier_id}
                            invalid={
                              !!(
                                formik.touched.supplier_id &&
                                formik.errors.supplier_id
                              )
                            }
                          >
                            <option value="">Select Supplier...</option>
                            {supplierData &&
                              supplierData.map((supplier) => (
                                <option key={supplier.id} value={supplier.id}>
                                  {supplier.name}
                                </option>
                              ))}
                          </Input>
                          {formik.touched.supplier_id &&
                            formik.errors.supplier_id && (
                              <FormFeedback>
                                {formik.errors.supplier_id}
                              </FormFeedback>
                            )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col sm="6">
                        <div className="mb-3">
                          <BaseInput
                            id="driver_name"
                            label="Driver Name"
                            name="driver_name"
                            type="text"
                            value={formik.values.driver_name}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.driver_name &&
                              !!formik.errors.driver_name
                            }
                            errorMessage={formik.errors.driver_name}
                          />
                        </div>
                        <div className="mb-3">
                          <BaseInput
                            id="vehicle_number"
                            label="Vehicle Number"
                            name="vehicle_number"
                            type="text"
                            value={formik.values.vehicle_number}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.vehicle_number &&
                              !!formik.errors.vehicle_number
                            }
                            errorMessage={formik.errors.vehicle_number}
                          />
                        </div>
                      </Col>
                      <Col sm="6">
                        <div className="mb-3">
                          <BaseInput
                            id="tracking_number"
                            label="Tracking Number"
                            name="tracking_number"
                            type="text"
                            value={formik.values.tracking_number}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.tracking_number &&
                              !!formik.errors.tracking_number
                            }
                            errorMessage={formik.errors.tracking_number}
                          />
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="expected_delivery_date">
                            Expected Delivery Date
                          </Label>
                          <Flatpickr
                            id="expected_delivery_date"
                            name="expected_delivery_date"
                            placeholder="Select delivery date"
                            value={
                              formik.values.expected_delivery_date || undefined
                            } // Set value as undefined if null
                            onChange={(date: Date[]) => {
                              // Check if a date is selected and format it as YYYY-MM-DD
                              const formattedDate = date.length
                                ? date[0].toISOString().split("T")[0]
                                : null;
                              formik.setFieldValue(
                                "expected_delivery_date",
                                formattedDate
                              );
                            }}
                            options={{
                              dateFormat: "Y-m-d",
                              altFormat: "F j, Y",
                              altInput: true,
                            }} // Flatpickr date format
                            className="form-control"
                          />

                          {formik.touched.expected_delivery_date &&
                            formik.errors.expected_delivery_date && (
                              <span className="text-danger">
                                {formik.errors.expected_delivery_date}
                              </span>
                            )}
                        </div>
                      </Col>
                      <Col sm="12">
                        <div className="mb-3">
                          <Label htmlFor="payment_status">Payment Status</Label>
                          <Input
                            id="payment_status"
                            name="payment_status"
                            type="select"
                            value={formik.values.payment_status}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.payment_status &&
                              !!formik.errors.payment_status
                            }
                          >
                            <option value="">Select Payment Status...</option>
                            <option key="1" value="pending">
                              Pending
                            </option>
                            <option key="2" value="completed">
                              Completed
                            </option>
                          </Input>
                          {formik.touched.payment_status &&
                            formik.errors.payment_status && (
                              <FormFeedback>
                                {formik.errors.payment_status}
                              </FormFeedback>
                            )}
                        </div>
                      </Col>
                      <Col sm="12">
                        <div className="mb-3">
                          <Label htmlFor="remarks">Remarks</Label>
                          <Input
                            id="remarks"
                            name="remarks"
                            type="textarea"
                            value={formik.values.remarks}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.remarks && !!formik.errors.remarks
                            }
                          />
                          {formik.touched.remarks && formik.errors.remarks && (
                            <FormFeedback>{formik.errors.remarks}</FormFeedback>
                          )}
                        </div>
                      </Col>{" "}
                      <Col sm="12">
                        <div className="mb-3">
                          <Label htmlFor="origin">Origin of supplies</Label>
                          <Input
                            id="origin"
                            name="origin"
                            type="text"
                            placeholder="eg. Accra"
                            value={formik.values.origin}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.origin && !!formik.errors.origin
                            }
                          />
                          {formik.touched.origin && formik.errors.origin && (
                            <FormFeedback>{formik.errors.origin}</FormFeedback>
                          )}
                        </div>
                      </Col>
                      <Col sm="12">
                        <div className="mb-3">
                          <Label htmlFor="proof_of_delivery">
                            Proof of Delivery
                          </Label>
                          <Input
                            id="proof_of_delivery"
                            name="proof_of_delivery"
                            type="text"
                            value={formik.values.proof_of_delivery}
                            onChange={formik.handleChange}
                            invalid={
                              formik.touched.proof_of_delivery &&
                              !!formik.errors.proof_of_delivery
                            }
                          />
                          {formik.touched.proof_of_delivery &&
                            formik.errors.proof_of_delivery && (
                              <FormFeedback>
                                {formik.errors.proof_of_delivery}
                              </FormFeedback>
                            )}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            {/* weighbill items */}
            <WeighbillItemsTable
              data={billItems}
              onAddItemClick={handleToggleModal}
              onDeleteItemClick={onClickDelete}
              onEditItemClick={onClickEdit}
            />

            {/* Button Section */}
            <div className="d-flex justify-content-center mt-3">
              <Button
                type="submit"
                color="primary"
                className="w-20 me-1"
                style={{ width: "20%" }}
              >
                {loading ? "Submitting ..." : "Submit"}
              </Button>
              <Button
                color="secondary"
                className="w-20 ms-1"
                style={{ width: "20%" }}
                onClick={handleCancel} // Handle cancel action
              >
                Cancel
              </Button>
            </div>
          </Form>
          <AddWeighBillItemModal
            isOpen={showWeighBillItemModal}
            isEdit={isEdit}
            itemIndex={itemIndex}
            editDetails={editDetails}
            toggle={toggle}
            newWeighBillItem={setBillItems}
            setIsEdit={setIsEdit}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default EditWeighBill;
